import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  NINETY,
  FAILURE,
  GROUNDING,
  GATE,
  PROBE,
  HONEST,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const fill = interpolate(frame, [30, 80], [0.06, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="solo · enterprise rag" color={COLORS.cyan} />
          {/* progress bar: 90% easy → last 10% is the work */}
          <div
            style={{
              width: 1180,
              height: 26,
              border: `1px solid ${COLORS.line}`,
              borderRadius: 8,
              overflow: "hidden",
              display: "flex",
              marginBottom: 34,
            }}
          >
            <div style={{ width: "90%", background: COLORS.line }} />
            <div
              style={{
                width: "10%",
                background: COLORS.amber,
                opacity: fill,
                boxShadow: `0 0 24px -4px ${COLORS.amber}`,
              }}
            />
          </div>
          <Big size={96}>
            The last{" "}
            <span style={{ color: COLORS.amber }}>10%</span> is{" "}
            <span style={{ color: COLORS.cyan }}>trust</span>,
            <br />
            not code
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — THE EASY 90% */
const STACK: PipeNode[] = [
  { label: "ingest", value: "REPOS+DOCS", accent: "cyan" },
  { label: "chunk", value: "STRUCTURAL", accent: "cyan" },
  { label: "index", value: "pgvector", sub: "hybrid search", accent: "green" },
  { label: "serve", value: "CHAT + MCP", sub: "missions · agents", accent: "violet" },
];
export const Scene02Ninety: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.ninety.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="the easy 90% · known patterns" color={COLORS.cyan} />
      <div style={{ width: 1660 }}>
        <PipelineGraph nodes={STACK} startFrame={8} stepFrames={24} nodeWidth={220} />
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 30,
          color: COLORS.dim,
          marginTop: 40,
          textAlign: "center",
        }}
      >
        a demo that answers questions <span style={{ color: COLORS.text }}>looks finished</span> — it isn't
      </div>
    </AbsoluteFill>
    <Caption lines={NINETY} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — THE FAILURE: confident-wrong */
export const Scene03Failure: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = interpolate(frame, [200, 230], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.failure.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the last 10% · what almost shipped" color={COLORS.red} />
          <Panel accent="red" title="generated mission output" width={1080}>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 34,
                lineHeight: 1.5,
                color: COLORS.text,
                padding: "8px 6px",
              }}
            >
              “…trusted by{" "}
              <span
                style={{
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  color: COLORS.red,
                }}
              >
                93,000+
              </span>{" "}
              teams, citing{" "}
              <span style={{ color: COLORS.cyan }}>[Source 4]</span>…”
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 20,
                  color: COLORS.dim,
                  marginTop: 18,
                }}
              >
                ↳ that number appears in <span style={{ color: COLORS.red }}>zero</span> source documents
              </div>
            </div>
          </Panel>
          <div
            style={{
              marginTop: 30,
              opacity: stamp,
              transform: `scale(${0.9 + stamp * 0.1})`,
              fontFamily: FONTS.mono,
              fontSize: 30,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: COLORS.red,
              border: `2px solid ${COLORS.red}`,
              padding: "8px 22px",
              borderRadius: 8,
            }}
          >
            fluent · cited · fabricated
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FAILURE} accent="red" />
    </AbsoluteFill>
  );
};

/* 04 — GROUNDING: numeric hard-block */
const GROUND_CODE = `// src/lib/missions/contracts/grounding-profile.ts
export const GroundingProfileSchema = z.object({
  enabled: z.boolean().default(false),
  numeric_grounding: z
    .enum(['hard_block', 'off'])
    .default('hard_block'),          // <- the guard
  require_external_attribution: z.boolean().default(true),
})

// any number with no source in retrieved context
// fails the draft — it is NOT a prompt, it BLOCKS.
function checkNumericGrounding(draft, context) {
  if (profile.numeric_grounding !== 'hard_block') return []
  return ungroundedNumbers(draft, context) // -> violations
}`;
export const Scene04Grounding: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.grounding.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 30 }}>
      <Kicker text="migration 187 · grounding contract" color={COLORS.green} />
      <CodePanel
        code={GROUND_CODE}
        language="tsx"
        filename="grounding-profile.ts"
        highlightLines={[5, 6]}
        revealFrames={220}
        fontSize={23}
        width={1260}
      />
    </AbsoluteFill>
    <Caption lines={GROUNDING} accent="green" />
  </AbsoluteFill>
);

/* 05 — THE OUTPUT GATE */
const GATE_PIPE: PipeNode[] = [
  { label: "agent draft", value: "OUTPUT", accent: "cyan" },
  { label: "gate", value: "verify-output", sub: "grounding check", accent: "amber" },
  { label: "refuse", value: "BLOCK", sub: "record reason", accent: "red" },
  { label: "send", value: "gmail-send", sub: "only if clean", accent: "green" },
];
export const Scene05Gate: React.FC = () => {
  const frame = useCurrentFrame();
  const show = interpolate(frame, [150, 180], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.gate.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
        <Kicker text="the gate in front of every send" color={COLORS.amber} />
        <div style={{ width: 1700 }}>
          <PipelineGraph nodes={GATE_PIPE} startFrame={6} stepFrames={22} nodeWidth={230} />
        </div>
        <div style={{ display: "flex", gap: 28, marginTop: 46, opacity: show, alignItems: "baseline" }}>
          <MetricCounter value={35} startFrame={155} durationFrames={45} accent="amber" fontSize={120} />
          <Callout label="record-gate-block · mig 222" accent="amber" width={420}>
            historical blocks now in a structured <b style={{ color: COLORS.text }}>gate_block_events</b> table
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={GATE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 06 — THE RECENCY PROBE */
const PROBE_CODE = `// src/lib/eval/recency-probe.ts  — black-box, no LLM
// "faithful to its sources" != "retrieved the RIGHT source"
interface RecencyProbe {
  freshMarker: string  // e.g. 'loop_overview'  (mig 220, recent)
  staleMarker: string  // e.g. 'groupDocsByTheme' (the stale doc)
}

// PASS only if the fresh doc out-ranks the stale distractor
// in the ordered chunks from retrieveRelevantChunks().
// Survives a graph-backend swap: it checks the CONTRACT,
// not the machinery underneath.`;
export const Scene06Probe: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.probe.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 30 }}>
      <Kicker text="the signal no grader had" color={COLORS.violet} />
      <CodePanel
        code={PROBE_CODE}
        language="tsx"
        filename="recency-probe.ts"
        highlightLines={[8, 9]}
        revealFrames={240}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={PROBE} accent="violet" />
  </AbsoluteFill>
);

/* 07 — THE HONEST REALITY CHECK */
export const Scene07Honest: React.FC = () => {
  const frame = useCurrentFrame();
  const c2 = interpolate(frame, [170, 200], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honest.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the discipline · definition of shipped" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="red" title="looked shipped" width={560}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 23, lineHeight: 1.8, color: COLORS.text }}>
              <div>12-kind learning loop</div>
              <div style={{ color: COLORS.dim }}>UI renders · approve works</div>
              <div style={{ color: COLORS.red, marginTop: 6 }}>
                11 of 12 handlers = no-op
              </div>
              <div style={{ color: COLORS.dim, fontSize: 19 }}>“dry nozzles”</div>
            </div>
          </Panel>
          <div style={{ opacity: c2, alignSelf: "center" }}>
            <Panel accent="lime" title="actually shipped" width={560}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 23, lineHeight: 1.8, color: COLORS.text }}>
                <div>observable end-to-end</div>
                <div style={{ color: COLORS.dim }}>no “run this script first”</div>
                <div style={{ color: COLORS.lime, marginTop: 6 }}>
                  rule written into CLAUDE.md
                </div>
                <div style={{ color: COLORS.dim, fontSize: 19 }}>so I'd stop fooling myself</div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={HONEST} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={74}>
          Features are table stakes.
          <br />
          <span style={{ color: COLORS.cyan }}>Trust is the product.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
